import React, { useMemo } from "react";
import { BankAccountsStatus, Invoice } from '@/types/payments';
import { useInvoiceManagement } from '@/hooks/useInvoiceManagement';
import { useInvoiceDetailsDialog } from '@/hooks/useInvoiceDetailsDialog';
import { usePaymentDateManager } from '@/hooks/usePaymentDateManager';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import PaymentDialogs from './PaymentDialogs';
import InvoiceDetailsDialog from './InvoiceDetailsDialog';
import AccountPayablesContent from './AccountPayablesContent';
import { formatCurrency } from '@/utils/paymentUtils';

interface PayablesTableSectionProps {
  invoices: Invoice[];
  setInvoices: React.Dispatch<React.SetStateAction<Invoice[]>>;
  mode?: 'unpaid' | 'all';
  showTabs?: boolean;
  showFilters?: boolean;
  refreshData?: () => Promise<void>;
  /**
   * If provided, only show invoices with these statuses and hide tabs.
   * Example: ['unpaid', 'overdue']
   */
  statusFilter?: string[];
}

const PayablesTableSection: React.FC<PayablesTableSectionProps> = ({
  invoices,
  setInvoices,
  mode = 'all',
  showTabs = true,
  showFilters = true,
  refreshData,
  statusFilter,
}) => {
  // Filter for unpaid if needed (legacy mode)
  const filteredInvoices = useMemo(() => {
    if (statusFilter && statusFilter.length > 0) {
      return invoices.filter(inv => statusFilter.includes(inv.status));
    }
    if (mode === 'unpaid') {
      return invoices.filter(inv => inv.status === 'unpaid' || inv.status === 'overdue');
    }
    return invoices;
  }, [invoices, mode, statusFilter]);

  // Use invoice management for state/handlers
  const {
    invoices: managedInvoices,
    selectedInvoices,
    sortConfig,
    paymentDialogOpen,
    setPaymentDialogOpen,
    cancelDialogOpen,
    setCancelDialogOpen,
    actionInvoiceId,
    toggleSelectInvoice,
    toggleSelectAll,
    handleSort,
    renderSortIcon,
    handleSingleCancel,
    handleSingleExport,
    handleDownloadInvoice,
    processPayment,
    processCancellation,
    handleBulkPayment,
    handleBulkCancel,
    handleBulkExport,
    setInvoices: setManagedInvoices
  } = useInvoiceManagement(filteredInvoices, 'dueDate', 'desc');

  // Payment date manager - returns adjusted invoices and update handler factory
  const { adjustedInvoices, createUpdatePaymentDate } = usePaymentDateManager(managedInvoices);

  // Create the update handler with our setState function
  const updatePaymentDate = createUpdatePaymentDate(setManagedInvoices);

  // Bank accounts for gating "Paid externally" action. #498 whole-branch review finding I2: must
  // distinguish "still loading" / "fetch failed or bank consent expired" from "genuinely no
  // accounts connected" — see BankAccountsStatus's docstring (src/types/payments.ts). Silently
  // treating loading/error as `false` would route a company whose accounts ARE connected through
  // the trusted, unaudited "paid externally" write with no auto_match_decisions row at all.
  const { accounts, loadingAccounts, accountsError, consentRequiredError } = useBankAccounts();
  const hasBankAccounts: BankAccountsStatus = loadingAccounts
    ? "loading"
    : accountsError || consentRequiredError
    ? "error"
    : accounts.length > 0;

  // Invoice details dialog
  const {
    detailsDialogOpen,
    selectedInvoice,
    setDetailsDialogOpen,
    handleRowClick
  } = useInvoiceDetailsDialog();

  return (
    <>
      <AccountPayablesContent
        invoices={adjustedInvoices}
        setInvoices={setManagedInvoices}
        selectedInvoices={selectedInvoices}
        sortConfig={sortConfig}
        handleSort={handleSort}
        renderSortIcon={renderSortIcon}
        toggleSelectInvoice={toggleSelectInvoice}
        toggleSelectAll={toggleSelectAll}
        handleBulkPayment={handleBulkPayment}
        handleBulkCancel={handleBulkCancel}
        handleBulkExport={handleBulkExport}
        updatePaymentDate={updatePaymentDate}
        handleRowClick={handleRowClick}
        handleSingleCancel={handleSingleCancel}
        handleSingleExport={handleSingleExport}
        handleDownloadInvoice={handleDownloadInvoice}
        showTabs={showTabs && !statusFilter}
        statusFilter={statusFilter}
        refreshData={refreshData}
        hasBankAccounts={hasBankAccounts}
      />

      {/* Payment and Cancel Dialogs */}
      <PaymentDialogs
        paymentDialogOpen={paymentDialogOpen}
        setPaymentDialogOpen={setPaymentDialogOpen}
        cancelDialogOpen={cancelDialogOpen}
        setCancelDialogOpen={setCancelDialogOpen}
        actionInvoiceId={actionInvoiceId}
        selectedInvoices={selectedInvoices}
        invoices={adjustedInvoices}
        processPayment={processPayment}
        processCancellation={processCancellation}
        refreshData={refreshData}
      />

      {/* Invoice Details Dialog */}
      <InvoiceDetailsDialog
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        invoice={selectedInvoice}
        updatePaymentDate={updatePaymentDate}
        formatCurrency={formatCurrency}
      />
    </>
  );
};

export default PayablesTableSection;
